import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PenLine, Loader2 } from "lucide-react";
import { usersApi } from "@/lib/api";

/**
 * Auswahlfenster für gespeicherte Techniker-Unterschriften.
 * Ruft beim Öffnen alle Benutzer mit hinterlegter Unterschrift ab.
 */
export function SignaturePickerDialog({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    usersApi
      .getSignatures()
      .then((res) => setItems(Array.isArray(res.data) ? res.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid="pick-signature-btn">
          <PenLine className="w-4 h-4 mr-1" />
          Gespeicherte Unterschrift
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Unterschrift auswählen</DialogTitle>
          <DialogDescription>Wählen Sie eine gespeicherte Unterschrift aus der Liste.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Keine gespeicherten Unterschriften. Techniker können ihre Unterschrift unter
            <br />
            <span className="font-medium">Einstellungen → Meine Unterschrift</span> hinterlegen.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto py-1">
            {items.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => {
                  onSelect(u.signature);
                  setOpen(false);
                }}
                data-testid={`signature-option-${u.id}`}
                className="border rounded-lg p-3 hover:border-primary hover:bg-primary/5 transition-colors text-left"
              >
                <img
                  src={u.signature}
                  alt={u.full_name}
                  className="h-20 w-full object-contain bg-white rounded"
                />
                <p className="mt-2 text-sm font-medium truncate">{u.full_name}</p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SignaturePickerDialog;
